/* NK Cart Editorial v1.0. Mutations use line-item keys, never variant IDs.
   Shopify renders all prices. No external libraries or estimated cart totals. */
(() => {
  'use strict';
  if (customElements.get('nk-cart-editorial')) return;
  class NKCartEditorial extends HTMLElement {
    connectedCallback() { this.startFrame = requestAnimationFrame(() => this.mount()); }
    disconnectedCallback() {
      cancelAnimationFrame(this.startFrame);
      clearTimeout(this.noteTimer);
      this.events?.abort();
      // Do not automatically retry an interrupted cart mutation.
      this.request?.abort();
      this.mounted = false;
    }
    $(selector) { return this.querySelector(selector); }
    $$(selector) { return Array.from(this.querySelectorAll(selector)); }
    listen(target, type, callback) { target?.addEventListener(type,callback,{signal:this.events.signal}); }
    mount() {
      if (this.mounted || !this.isConnected) return;
      const data = this.$('[data-nkc-state]');
      if (!data) return;
      try { this.state=JSON.parse(data.textContent); } catch (_) { return; }
      this.labels=this.state.labels;
      this.events=new AbortController();
      this.mounted=true;
      this.busy=false;
      this.uncertain=false;
      this.noteDraft=this.$('[data-nkc-note]')?.value || '';
      this.noteSaved=this.noteDraft;
      this.classList.add('is-ready');
      this.listen(this,'click',e=>{
        const remove=e.target.closest('[data-nkc-remove]');
        const step=e.target.closest('[data-nkc-step]');
        if (remove && this.contains(remove)) {
          e.preventDefault();
          if (!this.busy && !this.uncertain) this.changeLine(remove.closest('[data-nkc-line]'),0,'remove');
        }
        if (step && this.contains(step) && !step.disabled && !this.busy && !this.uncertain) {
          const line=step.closest('[data-nkc-line]');
          const input=line.querySelector('[data-nkc-quantity]');
          const quantity=Number(input.dataset.lastValue)+(Number(input.step)||1)*Number(step.dataset.nkcStep);
          this.changeLine(line,quantity,step.dataset.nkcStep);
        }
      });
      this.listen(this,'change',e=>{
        if (e.target.matches('[data-nkc-quantity]')) {
          const input=e.target;
          if (this.busy || this.uncertain) { input.value=input.dataset.lastValue; return; }
          if (!input.checkValidity() || !Number.isInteger(Number(input.value))) {
            input.reportValidity();
            input.value=input.dataset.lastValue;
            this.error(this.labels.invalid,false);
            return;
          }
          this.changeLine(input.closest('[data-nkc-line]'),Number(input.value),'input');
        }
      });
      this.listen(this,'input',e=>{
        if (!e.target.matches('[data-nkc-note]')) return;
        this.noteDraft=e.target.value;
        clearTimeout(this.noteTimer);
        this.noteTimer=setTimeout(()=>this.saveNote(),750);
      });
      this.listen(this,'submit',e=>{
        if (!e.target.matches('[data-nkc-form]')) return;
        if (this.busy || this.uncertain) { e.preventDefault(); return; }
        clearTimeout(this.noteTimer);
        // Native form POST includes note, quantities and the checkout submitter.
        // Do not disable the submitter here: that would remove name="checkout".
        this.$('[data-nkc-checkout]')?.setAttribute('aria-busy','true');
      });
      this.listen(window,'pageshow',e=>{ if (e.persisted) this.refresh(); });
      this.updateCount(this.state.count);
    }
    sectionURL() {
      const url=new URL(location.href);
      // Keep the selected cart view and preview_theme_id in editor previews.
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      return url.pathname+url.search;
    }
    error(message,needsRefresh=true) {
      const box=this.$('[data-nkc-error]');
      box.hidden=!message;
      this.$('[data-nkc-error-text]').textContent=message || '';
      const refresh=this.$('[data-nkc-refresh]');
      refresh.hidden=!needsRefresh;
      refresh.href=location.href;
    }
    status(message) { this.$('[data-nkc-status]').textContent=message || ''; }
    setBusy(value) {
      this.busy=value;
      this.setAttribute('aria-busy',String(value));
      this.$$('[data-nkc-step], [data-nkc-checkout], [data-nkc-quantity], [data-nkc-note]').forEach(node=>{
        if (value) {
          node.dataset.nkcWasDisabled=String(Boolean(node.disabled));
          node.dataset.nkcWasReadonly=String(Boolean(node.readOnly));
          if (node.matches('button')) node.disabled=true;
          else node.readOnly=true;
        } else {
          if (node.matches('button')) node.disabled=node.dataset.nkcWasDisabled==='true';
          else node.readOnly=node.dataset.nkcWasReadonly==='true';
          delete node.dataset.nkcWasDisabled;
          delete node.dataset.nkcWasReadonly;
        }
      });
      if (this.uncertain) this.lockUncertain();
    }
    lockUncertain() {
      this.$$('[data-nkc-step], [data-nkc-checkout]').forEach(el=>{el.disabled=true;});
      this.$$('[data-nkc-quantity]').forEach(el=>{el.readOnly=true;});
    }
    async post(url,payload) {
      this.request=new AbortController();
      const timer=setTimeout(()=>this.request.abort(),18000);
      try {
        const response=await fetch(url,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(payload),signal:this.request.signal});
        let data;
        try { data=await response.json(); } catch (_) { throw new Error(this.labels.error); }
        if (!response.ok || data.status) {
          const error=new Error(typeof data.description==='string' ? data.description : typeof data.message==='string' ? data.message : this.labels.error);
          // A bundled-section HTTP 400 can follow an applied mutation.
          error.confirmedRejection=response.status>=400 && response.status<500 && !(response.status===400 && payload.sections);
          throw error;
        }
        if (!Array.isArray(data.items) || !Number.isFinite(data.item_count)) throw new Error(this.labels.error);
        return data;
      } finally { clearTimeout(timer); }
    }
    async changeLine(line,quantity,focusAction) {
      if (this.busy || this.uncertain || !line) return;
      const input=line.querySelector('[data-nkc-quantity]');
      if (input.dataset.locked==='true' && quantity!==0) return;
      const min=Number(input.min)||1;
      const step=Number(input.step)||1;
      const max=input.max ? Number(input.max) : Infinity;
      if (!Number.isInteger(quantity) || quantity<0 || (quantity!==0 && (quantity<min || quantity>max || (quantity-min)%step!==0))) {
        input.value=input.dataset.lastValue;
        this.error(this.labels.invalid,false);
        return;
      }
      if (quantity===Number(input.dataset.lastValue)) return;
      clearTimeout(this.noteTimer);
      this.noteDraft=this.$('[data-nkc-note]')?.value ?? this.noteDraft;
      const noteOpen=Boolean(this.$('[data-nkc-note-details]')?.open);
      const key=line.dataset.key;
      const index=this.$$('[data-nkc-line]').indexOf(line);
      this.error('');
      this.setBusy(true);
      this.status(this.labels.updating);
      let confirmed=false;
      try {
        const data=await this.post(this.dataset.changeUrl,{id:key,quantity,sections:[this.dataset.sectionId,'cart-item-count'],sections_url:this.sectionURL()});
        confirmed=true;
        await this.renderCart(data);
        const note=this.$('[data-nkc-note]');
        if (note) note.value=this.noteDraft;
        const noteDetails=this.$('[data-nkc-note-details]');
        if (noteDetails) noteDetails.open=noteOpen;
        this.updateCount(data.item_count);
        this.status(this.labels.updated);
        this.restoreFocus(key,index,focusAction);
        this.dispatchEvent(new CustomEvent('nk:cart-updated',{bubbles:true,detail:{cart:data}}));
      } catch(error) {
        if (!this.isConnected) return;
        this.uncertain=confirmed || !error.confirmedRejection;
        if (!this.uncertain) input.value=input.dataset.lastValue;
        this.error(this.uncertain ? this.labels.error : error.message,this.uncertain);
        this.status('');
      } finally {
        if (this.isConnected) {
          this.setBusy(false);
          if (!this.uncertain && this.noteDraft!==this.noteSaved) this.noteTimer=setTimeout(()=>this.saveNote(),150);
        }
      }
    }
    async fetchSections() {
      const url=new URL(this.sectionURL(),location.origin);
      url.searchParams.set('sections',`${this.dataset.sectionId},cart-item-count`);
      const controller=new AbortController();
      const timer=setTimeout(()=>controller.abort(),12000);
      try {
        const response=await fetch(url,{credentials:'same-origin',headers:{Accept:'application/json'},signal:controller.signal});
        if (!response.ok) throw new Error('Section read failed');
        return await response.json();
      } finally { clearTimeout(timer); }
    }
    async renderCart(cart) {
      let html=cart.sections?.[this.dataset.sectionId];
      if (!html) html=(await this.fetchSections())[this.dataset.sectionId];
      if (!html) throw new Error('Cart markup missing');
      const parsed=new DOMParser().parseFromString(html,'text/html');
      const fresh=parsed.getElementById(this.id);
      const content=fresh?.querySelector('[data-nkc-content]');
      if (!content || !content.querySelector('[data-nkc-state]')) throw new Error('Cart state missing');
      const state=JSON.parse(content.querySelector('[data-nkc-state]').textContent);
      this.$('[data-nkc-content]').replaceChildren(...Array.from(content.childNodes));
      this.state=state;
      this.labels=state.labels;
      // Newly rendered controls already carry their server-side disabled states.
      this.$$('[data-nkc-step], [data-nkc-checkout], [data-nkc-quantity], [data-nkc-note]').forEach(node=>{
        node.dataset.nkcWasDisabled=String(Boolean(node.disabled));
        node.dataset.nkcWasReadonly=String(Boolean(node.readOnly));
      });
    }
    restoreFocus(key,index,action) {
      const lines=this.$$('[data-nkc-line]');
      const line=lines.find(el=>el.dataset.key===key) || lines[Math.min(index,lines.length-1)];
      if (!line) { this.$('.nkc-empty a')?.focus({preventScroll:true}); return; }
      const selector=action==='input' ? '[data-nkc-quantity]' : action==='remove' ? '[data-nkc-remove]' : `[data-nkc-step="${action}"]`;
      (line.querySelector(selector)||line.querySelector('a'))?.focus({preventScroll:true});
    }
    async saveNote() {
      if (!this.isConnected || this.uncertain || this.noteDraft===this.noteSaved) return;
      if (this.busy) { this.noteTimer=setTimeout(()=>this.saveNote(),250); return; }
      const note=this.noteDraft;
      this.setBusy(true);
      try {
        await this.post(this.dataset.updateUrl,{note});
        this.noteSaved=note;
        this.status(this.labels.saved);
      } catch (_) {
        // Note updates are idempotent. The native checkout form keeps the draft.
        if (this.isConnected) this.error(this.labels.noteError,false);
      } finally { if (this.isConnected) this.setBusy(false); }
    }
    updateCount(count) {
      // Verified against the uploaded Cascade 5.1.0 cart-count.js store.
      if (window.theme) window.theme.cartItemCount=count;
      try { const store=window.Alpine?.store('cart_count'); if (store) store.count=count; } catch (_) { /* Header without Alpine still updates on navigation. */ }
    }
    async refresh() {
      if (this.busy) return;
      this.error(''); this.setBusy(true);
      try {
        await this.renderCart({sections:await this.fetchSections()});
        this.noteDraft=this.$('[data-nkc-note]')?.value || '';
        this.noteSaved=this.noteDraft;
        this.uncertain=false;
        this.updateCount(this.state.count);
      } catch (_) { this.uncertain=true; this.error(this.labels.error); }
      finally { this.setBusy(false); }
    }
  }
  customElements.define('nk-cart-editorial',NKCartEditorial);
})();
